import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Pause, Play } from "lucide-react";

import {
    getMeditationAudio,
    getMeditationBackgroundAudio,
} from "../../application/portalServices";
import { BACKGROUND_TRACK_GAIN, backgroundTrackSrc, BackgroundTrackId } from "../../../meditation";
import type { PortalMeditation } from "../../domain/types";

type Props = { meditation: PortalMeditation };

/**
 * Playing, at home, what was recorded for the session.
 *
 * One button and nothing else. A patient putting a meditation on before bed is
 * about to close their eyes, so the screen they are leaving should not be a
 * console: no scrubber to fiddle with, no counter to watch, no way to arrive
 * somewhere in the middle of a breathing instruction.
 *
 * Voice and bed are two elements rather than one mixed file, the same as in
 * the session, so the balance the therapist set is the balance heard here. The
 * bytes are asked for on the first press: most visits to this page are to read
 * the script, and a recording fetched on render would be a download nobody
 * asked for.
 */
export default function PortalMeditationPlayer({ meditation }: Props) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [playing, setPlaying] = useState(false);
    /*
     * Two different problems. "Missing" is a recording whose file never
     * finished uploading — nothing the patient can do about it, so the message
     * points at the person who can — while "failed" is this attempt, which is
     * worth trying again.
     */
    const [failure, setFailure] = useState<"missing" | "failed" | null>(null);

    const voiceRef = useRef<HTMLAudioElement | null>(null);
    const bedRef = useRef<HTMLAudioElement | null>(null);
    /** Object URLs we made, so the browser can let the bytes go afterwards. */
    const madeUrls = useRef<string[]>([]);

    const stop = () => {
        voiceRef.current?.pause();
        bedRef.current?.pause();
        setPlaying(false);
    };

    // Nothing this page started should outlive it — a meditation still playing
    // after the patient navigated away is the worst version of this feature.
    useEffect(
        () => () => {
            voiceRef.current?.pause();
            bedRef.current?.pause();
            madeUrls.current.forEach((url) => URL.revokeObjectURL(url));
            madeUrls.current = [];
        },
        []
    );

    const objectUrl = (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        madeUrls.current.push(url);
        return url;
    };

    const bedSource = async (): Promise<string | null> => {
        if (meditation.has_custom_background) {
            const data = await getMeditationBackgroundAudio(meditation.id);
            return objectUrl(data);
        }
        return backgroundTrackSrc(
            meditation.background_track as BackgroundTrackId
        );
    };

    const toggle = async () => {
        if (playing) {
            stop();
            return;
        }
        if (voiceRef.current) {
            void voiceRef.current.play().catch(() => setPlaying(false));
            void bedRef.current?.play().catch(() => undefined);
            setPlaying(true);
            return;
        }

        setLoading(true);
        setFailure(null);
        try {
            const data = await getMeditationAudio(meditation.id);
            const voice = new Audio(objectUrl(data));
            voice.onended = stop;
            voiceRef.current = voice;

            const bed = await bedSource();
            if (bed) {
                const music = new Audio(bed);
                music.loop = true;
                music.volume = meditation.background_volume || BACKGROUND_TRACK_GAIN;
                bedRef.current = music;
            }

            await voice.play();
            void bedRef.current?.play().catch(() => undefined);
            setPlaying(true);
        } catch (cause) {
            const status = (cause as { response?: { status?: number } })?.response
                ?.status;
            setFailure(status === 404 ? "missing" : "failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-wrap items-center gap-3">
            <button
                type="button"
                onClick={() => void toggle()}
                disabled={loading}
                className="flex items-center gap-2 rounded-xl bg-accent px-5 py-3 text-sm font-semibold text-accent-contrast transition-opacity hover:opacity-90 disabled:opacity-50"
            >
                {loading ? (
                    <Loader2 size={18} className="animate-spin" aria-hidden="true" />
                ) : playing ? (
                    <Pause size={18} aria-hidden="true" />
                ) : (
                    <Play size={18} aria-hidden="true" />
                )}
                {t(
                    playing
                        ? "portal.meditations.pause"
                        : "portal.meditations.play"
                )}
            </button>

            {failure && (
                <p role="alert" className="text-sm text-red-600">
                    {t(
                        failure === "missing"
                            ? "portal.meditations.recordingMissing"
                            : "portal.meditations.playFailed"
                    )}
                </p>
            )}
        </div>
    );
}
